/**
 * FindRisk (Finnish Diabetes Risk Score) - Type 2 diabetes risk assessment.
 * Based on Lindström & Tuomilehto, Diabetes Care 2003.
 */

export type FindRiskGender = 'male' | 'female';
export type FindRiskFamilyHistory = 'none' | 'secondary' | 'primary';

export interface FindRiskInput {
  age: number;
  bmi: number;
  waistCm: number;
  gender: FindRiskGender;
  physicalActivity: boolean; // true = yes, 30+ min/day
  vegetablesDaily: boolean;
  bpMedication: boolean;
  highBloodGlucose: boolean;
  familyHistory: FindRiskFamilyHistory;
}

export type FindRiskCategory =
  | 'low'
  | 'mild'
  | 'moderate'
  | 'high'
  | 'veryHigh';

export interface FindRiskResult {
  score: number;
  category: FindRiskCategory;
}

function agePoints(age: number): number {
  if (age < 45) return 0;
  if (age < 55) return 2;
  if (age < 65) return 3;
  return 4;
}

function bmiPoints(bmi: number): number {
  if (bmi < 25) return 0;
  if (bmi < 30) return 1;
  return 3;
}

function waistPoints(waistCm: number, gender: FindRiskGender): number {
  if (gender === 'male') {
    if (waistCm < 94) return 0;
    if (waistCm <= 102) return 3;
    return 4;
  }
  if (waistCm < 80) return 0;
  if (waistCm <= 88) return 3;
  return 4;
}

export function calculateFindRiskScore(input: FindRiskInput): FindRiskResult {
  let score = 0;
  score += agePoints(input.age);
  score += bmiPoints(input.bmi);
  score += waistPoints(input.waistCm, input.gender);
  score += input.physicalActivity ? 0 : 2;
  score += input.vegetablesDaily ? 0 : 1;
  score += input.bpMedication ? 2 : 0;
  score += input.highBloodGlucose ? 5 : 0;
  if (input.familyHistory === 'secondary') score += 3;
  else if (input.familyHistory === 'primary') score += 5;

  const category = getFindRiskCategory(score);
  return { score, category };
}

export function getFindRiskCategory(score: number): FindRiskCategory {
  if (score <= 7) return 'low';
  if (score <= 11) return 'mild';
  if (score <= 14) return 'moderate';
  if (score <= 20) return 'high';
  return 'veryHigh';
}
